import React, { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box, Typography, Tooltip, Collapse, Divider,
} from '@mui/material';
import {
  ChevronLeft, ChevronRight, ExpandMore, ExpandLess,
  Dashboard, Storefront, Category, ShoppingCart,
  DirectionsBike, People, Radar, Star, Bolt, AutoAwesome,
  BrokenImage, PointOfSale, Inventory2, BarChart, LocalOffer,
  AccessTime, Loyalty, LocalShipping, Campaign, ViewCarousel,
  Settings, Analytics, Group, LocalFireDepartment, NotificationsActive,
  History, MonitorHeart, TrendingUp, Warehouse,
} from '@mui/icons-material';
import { useThemeMode } from '../context/ThemeContext';

const BRAND = 'linear-gradient(135deg,#6366f1 0%,#8b5cf6 100%)';

const SECTIONS = [
  {
    id: 'pos',
    label: 'POS & SALES',
    items: [
      { to: '/admin/pos',       label: 'POS Terminal',     icon: PointOfSale },
      { to: '/admin/orders',    label: 'Online Orders',    icon: ShoppingCart },
      { to: '/admin/reports',   label: 'Sales Reports',    icon: BarChart },
      { to: '/admin/shifts',    label: 'Shift Management', icon: AccessTime },
    ],
  },
  {
    id: 'store',
    label: 'STORE',
    items: [
      { to: '/admin',                   label: 'Dashboard',         icon: Dashboard,       exact: true },
      { to: '/admin/products',          label: 'Products',          icon: Storefront },
      { to: '/admin/categories',        label: 'Categories',        icon: Category },
      { to: '/admin/inventory',         label: 'Inventory',         icon: Warehouse },
      { to: '/admin/discounts',         label: 'Discounts',         icon: LocalOffer },
      { to: '/admin/suppliers',         label: 'Suppliers & POs',   icon: LocalShipping },
      { to: '/admin/customers',         label: 'Loyalty Customers', icon: Loyalty },
      { to: '/admin/flash-deals',       label: 'Flash Deals',       icon: Bolt },
      { to: '/admin/featured-products', label: 'Featured',          icon: Star },
      { to: '/admin/quick-picks',       label: 'Quick Picks',       icon: AutoAwesome },
      { to: '/admin/deals',             label: 'Deals of the Day',  icon: LocalFireDepartment },
    ],
  },
  {
    id: 'marketing',
    label: 'MARKETING',
    items: [
      { to: '/admin/content',             label: 'Hero Banners',        icon: ViewCarousel },
      { to: '/admin/promo',               label: 'Promo Strip',          icon: Campaign },
      { to: '/admin/campaigns',           label: 'Campaigns',           icon: Campaign },
      { to: '/admin/segments',            label: 'Segments',            icon: Group },
      { to: '/admin/notification-center', label: 'Notifications',       icon: NotificationsActive },
      { to: '/admin/analytics',           label: 'Analytics',           icon: Analytics },
    ],
  },
  {
    id: 'admin',
    label: 'ADMIN',
    items: [
      { to: '/admin/users',             label: 'Users',           icon: People },
      { to: '/admin/riders',            label: 'Riders',          icon: DirectionsBike },
      { to: '/admin/rider-orders',      label: 'Rider Orders',    icon: DirectionsBike },
      { to: '/admin/app-config',        label: 'App Config',      icon: Settings },
      { to: '/admin/audit-log',         label: 'Audit Log',       icon: History },
      { to: '/admin/system',            label: 'System',          icon: MonitorHeart },
      { to: '/admin/live-tracking',     label: 'Live Tracking',   icon: TrendingUp },
      { to: '/admin/customer-tracking', label: 'Customer Map',    icon: Radar },
      { to: '/admin/image-recovery',    label: 'Image Recovery',  icon: BrokenImage },
      { to: '/admin/profile',           label: 'My Profile',      icon: People },
    ],
  },
];

const STORAGE_KEY = 'mtaani_sections_collapsed';
function loadSectionState() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}; } catch { return {}; }
}

export default function AdminSidebar({ collapsed, onToggle, width, expandedWidth, collapsedWidth }) {
  const location = useLocation();
  const { mode } = useThemeMode();
  const dark = mode === 'dark';
  const [sectionCollapsed, setSectionCollapsed] = useState(loadSectionState);

  const toggleSection = (id) => {
    const next = { ...sectionCollapsed, [id]: !sectionCollapsed[id] };
    setSectionCollapsed(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
  };

  const isActive = (item) =>
    item.exact
      ? location.pathname === item.to || location.pathname === `${item.to}/`
      : location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);

  const navItem = (item) => {
    const active = isActive(item);
    const Icon = item.icon;
    const node = (
      <Box
        key={item.to}
        component={RouterLink}
        to={item.to}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: collapsed ? 0 : 1.5,
          px: collapsed ? 0 : 1.5,
          py: 0.8,
          mb: 0.3,
          borderRadius: 1.5,
          cursor: 'pointer',
          textDecoration: 'none',
          justifyContent: collapsed ? 'center' : 'flex-start',
          position: 'relative',
          transition: 'all 0.15s ease',
          color: active ? 'primary.main' : 'text.secondary',
          bgcolor: active
            ? dark ? 'rgba(99,102,241,0.14)' : 'rgba(99,102,241,0.08)'
            : 'transparent',
          '&::before': active ? {
            content: '""',
            position: 'absolute',
            left: collapsed ? 0 : -8,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 3,
            height: 20,
            bgcolor: 'primary.main',
            borderRadius: '0 3px 3px 0',
          } : {},
          '&:hover': {
            bgcolor: active
              ? dark ? 'rgba(99,102,241,0.18)' : 'rgba(99,102,241,0.12)'
              : dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
            color: active ? 'primary.main' : 'text.primary',
          },
        }}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 28,
          height: 28,
          flexShrink: 0,
          borderRadius: 1.5,
          bgcolor: active
            ? dark ? 'rgba(99,102,241,0.2)' : 'rgba(99,102,241,0.12)'
            : 'transparent',
          transition: 'background 0.15s ease',
        }}>
          <Icon sx={{ fontSize: 17, color: 'inherit' }} />
        </Box>
        {!collapsed && (
          <Typography sx={{
            fontSize: 13,
            fontWeight: active ? 600 : 500,
            color: 'inherit',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: 1,
          }}>
            {item.label}
          </Typography>
        )}
      </Box>
    );

    return collapsed ? (
      <Tooltip key={item.to} title={item.label} placement="right" arrow>
        {node}
      </Tooltip>
    ) : node;
  };

  return (
    <Box
      sx={{
        width,
        minWidth: width,
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: dark ? '#111827' : '#ffffff',
        borderRight: `1px solid ${dark ? 'rgba(148,163,184,0.1)' : 'rgba(0,0,0,0.07)'}`,
        transition: 'width 0.22s cubic-bezier(0.4,0,0.2,1), min-width 0.22s cubic-bezier(0.4,0,0.2,1)',
        overflow: 'hidden',
        flexShrink: 0,
        zIndex: 100,
      }}
    >
      {/* Logo + collapse button */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'space-between',
        px: collapsed ? 0 : 2,
        height: 56,
        borderBottom: `1px solid ${dark ? 'rgba(148,163,184,0.08)' : 'rgba(0,0,0,0.06)'}`,
        flexShrink: 0,
      }}>
        {!collapsed && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, overflow: 'hidden' }}>
            <Box sx={{
              width: 28, height: 28, borderRadius: 1.5,
              background: BRAND,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14, flexShrink: 0,
            }}>
              🏪
            </Box>
            <Box sx={{ overflow: 'hidden' }}>
              <Typography sx={{ fontWeight: 800, fontSize: 14, color: 'text.primary', lineHeight: 1.1, letterSpacing: '-0.01em' }}>
                Mtaani
              </Typography>
              <Typography sx={{ fontSize: 10, color: 'text.secondary', fontWeight: 500 }}>
                Admin Console
              </Typography>
            </Box>
          </Box>
        )}
        <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right" arrow>
          <Box
            onClick={onToggle}
            sx={{
              width: 28, height: 28,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: 1.5,
              cursor: 'pointer',
              color: 'text.secondary',
              flexShrink: 0,
              transition: 'all 0.15s ease',
              '&:hover': { bgcolor: dark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)', color: 'text.primary' },
            }}
          >
            {collapsed ? <ChevronRight sx={{ fontSize: 18 }} /> : <ChevronLeft sx={{ fontSize: 18 }} />}
          </Box>
        </Tooltip>
      </Box>

      {/* Nav */}
      <Box sx={{
        flex: 1,
        overflow: 'auto',
        overflowX: 'hidden',
        py: 1.5,
        px: collapsed ? 0.75 : 1.5,
        '&::-webkit-scrollbar': { width: 3 },
        '&::-webkit-scrollbar-thumb': { bgcolor: 'transparent' },
        '&:hover::-webkit-scrollbar-thumb': { bgcolor: dark ? '#334155' : '#d1d5db' },
      }}>
        {SECTIONS.map((section, idx) => (
          <Box key={section.id} sx={{ mb: 0.5 }}>
            {idx > 0 && !collapsed && <Divider sx={{ my: 1.5 }} />}
            {idx > 0 && collapsed && <Box sx={{ height: 8 }} />}

            {/* Section header */}
            {!collapsed && (
              <Box
                onClick={() => toggleSection(section.id)}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  px: 1, py: 0.5, mb: 0.5,
                  cursor: 'pointer', borderRadius: 1.5,
                  userSelect: 'none',
                  '&:hover': { bgcolor: dark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)' },
                }}
              >
                <Typography sx={{
                  fontSize: 10, fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'text.disabled',
                }}>
                  {section.label}
                </Typography>
                {sectionCollapsed[section.id]
                  ? <ExpandMore sx={{ fontSize: 13, color: 'text.disabled' }} />
                  : <ExpandLess sx={{ fontSize: 13, color: 'text.disabled' }} />
                }
              </Box>
            )}

            <Collapse in={!sectionCollapsed[section.id]} timeout={200}>
              <Box>
                {section.items.map(navItem)}
              </Box>
            </Collapse>
          </Box>
        ))}
      </Box>

      {/* Footer */}
      {!collapsed && (
        <Box sx={{
          px: 1.5, py: 1.5,
          borderTop: `1px solid ${dark ? 'rgba(148,163,184,0.08)' : 'rgba(0,0,0,0.06)'}`,
          flexShrink: 0,
        }}>
          <Box sx={{
            p: 1.5, borderRadius: 2,
            background: dark ? 'rgba(99,102,241,0.1)' : 'rgba(99,102,241,0.06)',
            border: `1px solid ${dark ? 'rgba(99,102,241,0.2)' : 'rgba(99,102,241,0.15)'}`,
          }}>
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: 'primary.main' }}>
              Mtaani v3.0
            </Typography>
            <Typography sx={{ fontSize: 10.5, color: 'text.secondary', mt: 0.2 }}>
              Enterprise CMS
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}
